import * as React from 'react';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Button from '@mui/material/Button';
import { channelOptions, IMarketFormValues, marketFormSchema } from '../../../../@types/model/masterData/market/marketFormValues';
import { useAppSelector } from '../../../../@types/redux';
import { PillButton, ControlledTextField, ControlledMuiAutoCompleteSelect } from '@zz2/zz2-ui';
import { ControlledLabelledCheckBox } from '../../../../components/input/form/ControlledLabelledCheckBoxWrapper';
import CustomReactHookFormDevTools from '../../../../components/devtools/ReactHookFormDevTools';

interface IMarketFormProps {
    isLoading : boolean;
    initialValues : IMarketFormValues;
    onSubmit : (values : IMarketFormValues) => void;
    onClose : () => void;
}

const MarketForm = (props : IMarketFormProps) : React.ReactElement => {
    const schema = marketFormSchema;
    const { initialValues, onSubmit, onClose, isLoading } = props;

    const { handleSubmit, control, formState: { isValid, isDirty } } = useForm<IMarketFormValues>({
        defaultValues: initialValues,
        resolver: zodResolver(schema),
        mode: 'onChange',
    });

    const useRegionOptions = () => {
        const regions = useAppSelector(x => x.masterData.regions);
        const selectedOrganizationIds = useAppSelector(x => x.data.selectedOrganizationIds);
        return useMemo(() => {
            return regions
                ?.filter(x => x.isActive && selectedOrganizationIds?.some(y => x.organizationIds.some(z => y === z)))
                .map(x => ({ label: `(${x.code}) ${x.name}`, value: x.id })) ?? [];
        }, [regions, selectedOrganizationIds]);
    };
    const regionOptions = useRegionOptions();

    return (
        <div>
            <CustomReactHookFormDevTools control={control} />
            <form onSubmit={handleSubmit(onSubmit)} className={'p20'}>
                <div className={'fdc aic'}>
                    <ControlledTextField
                        className={'mb10 w500'}
                        name={'code'}
                        label={'Code'}
                        control={control}
                        defaultValue={initialValues.code} />
                    <ControlledTextField
                        className={'mb10 w500'}
                        name={'name'}
                        label={'Name'}
                        control={control}
                        defaultValue={initialValues.name} />
                    <ControlledMuiAutoCompleteSelect
                        className={'p0 mb10 w500'}
                        name={'channel'}
                        label={'Channel'}
                        control={control}
                        options={channelOptions}
                        defaultValues={initialValues.channel ?? null} />
                    <ControlledMuiAutoCompleteSelect
                        className={'p0 mb10 w500'}
                        name={'region'}
                        label={'Region'}
                        control={control}
                        options={regionOptions}
                        defaultValues={initialValues.region ?? null} />
                    <div className={'fdr jcfs mb5'}>
                        <ControlledLabelledCheckBox
                            className={'p0'}
                            name={'isActive'}
                            label={'Is Active'}
                            control={control}
                            defaultValue={initialValues.isActive} />
                    </div>
                    <div className={'fdr ml10 ais jcfe pt10 pb10'}>
                        <Button className={'cpd mr20'} type={'button'} onClick={onClose}>
                            CANCEL
                        </Button>
                        <PillButton
                            disabled={!isDirty || !isValid || isLoading}
                            className={'ml15 pl20 pr20 h35'}
                            text={'Save'}
                            type={'submit'}
                            color={'secondary'} />
                    </div>
                </div>
            </form>
        </div>
    );
};

export default MarketForm;
